import type { ReferencePlane } from '../../../../../reference/ReferencePlane';
import type { EllipseProjectionResult } from '../../../model/ProjectionResult';
import type { ResolvedEllipseReferenceSource } from '../SketchReferenceSourceResolver';
import type { SupportProjectionPreprocessor } from './SupportProjectionPreprocessor';
import {
	buildProjectedRotatedEllipseBasis,
	decomposeProjectedEllipseBasis,
	isProjectedLine,
	localPointToWorldCoordinate,
} from './SupportProjectionPreprocessor';

/**
 * 完整椭圆源投影预处理器。
 * 说明：
 * 1. 完整椭圆作为草图曲线源时，目标端仍应创建完整椭圆引用实体；
 * 2. 若目标平面投影退化为单线段，则闭合为 line，避免产生不可求解的零短轴椭圆；
 * 3. 该类只处理投影归一化，不创建对象、不写仓储。
 */
export class EllipseSupportProjectionPreprocessor
	implements SupportProjectionPreprocessor<ResolvedEllipseReferenceSource, EllipseProjectionResult>
{
	/**
	 * 执行完整椭圆投影。
	 *
	 * @param input 投影输入。
	 * @returns 成功时返回 ellipse / line，完全退化时返回 null。
	 */
	public projectToTargetPlane(input: {
		source: ResolvedEllipseReferenceSource;
		targetReferencePlane: ReferencePlane;
	}): EllipseProjectionResult | null {
		const projectedBasis = buildProjectedRotatedEllipseBasis({
			centerWorld: input.source.centerWorld,
			planeXAxisWorld: input.source.planeXAxisWorld,
			planeYAxisWorld: input.source.planeYAxisWorld,
			radiusX: input.source.radiusX,
			radiusY: input.source.radiusY,
			rotation: input.source.rotation,
			targetReferencePlane: input.targetReferencePlane,
		});
		const ellipse = decomposeProjectedEllipseBasis(projectedBasis);
		if (!ellipse) {
			return null;
		}

		if (isProjectedLine(ellipse)) {
			const startLocal = ellipse.centerLocal.clone()
				.addScaledVector(ellipse.majorAxisUnitLocal, -ellipse.radiusX);
			const endLocal = ellipse.centerLocal.clone()
				.addScaledVector(ellipse.majorAxisUnitLocal, ellipse.radiusX);
			return {
				sourceDescriptor: input.source.sourceDescriptor,
				sourceKind: 'ellipse',
				projectionResultKind: 'line',
				diagnostics: [],
				geometryPayload: {
					startWorld: localPointToWorldCoordinate(startLocal, input.targetReferencePlane),
					endWorld: localPointToWorldCoordinate(endLocal, input.targetReferencePlane),
				},
			};
		}

		return {
			sourceDescriptor: input.source.sourceDescriptor,
			sourceKind: 'ellipse',
			projectionResultKind: 'ellipse',
			diagnostics: [],
			geometryPayload: {
				centerWorld: localPointToWorldCoordinate(ellipse.centerLocal, input.targetReferencePlane),
				radiusX: ellipse.radiusX,
				radiusY: ellipse.radiusY,
				rotation: ellipse.rotation,
			},
		};
	}
}
